import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { tenantsApi } from '@/lib/api';
import { Clock, ArrowClockwise, ArrowRight, Warning } from '@phosphor-icons/react';

const SlaInput = ({ label, priority, fieldKey, policyState, onChange }) => {
  const totalMins = policyState[priority]?.[fieldKey] || 0;
  
  // Calculate value and unit
  let initialValue = totalMins;
  let initialUnit = 'mins';
  
  if (totalMins > 0) {
    if (totalMins % 1440 === 0) {
      initialValue = totalMins / 1440;
      initialUnit = 'days';
    } else if (totalMins % 60 === 0) {
      initialValue = totalMins / 60;
      initialUnit = 'hours';
    }
  }
  
  const [val, setVal] = useState(initialValue);
  const [unit, setUnit] = useState(initialUnit);
  
  // Sync if policyState changes externally
  useEffect(() => {
    let v = totalMins;
    let u = 'mins';
    if (totalMins > 0) {
      if (totalMins % 1440 === 0) {
        v = totalMins / 1440;
        u = 'days';
      } else if (totalMins % 60 === 0) {
        v = totalMins / 60;
        u = 'hours';
      }
    }
    setVal(v);
    setUnit(u);
  }, [totalMins]);

  const handleChange = (newVal, newUnit) => {
    setVal(newVal);
    setUnit(newUnit);
    
    let multiplier = 1;
    if (newUnit === 'hours') multiplier = 60;
    if (newUnit === 'days') multiplier = 1440;
    
    const mins = (parseInt(newVal) || 0) * multiplier;
    onChange(priority, fieldKey, mins);
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-bold uppercase text-slate-500">{label}</Label>
      <div className="flex gap-2">
        <Input
          type="number"
          min="1"
          value={val}
          onChange={(e) => handleChange(e.target.value, unit)}
          className="bg-white border-slate-300 flex-1 h-9"
        />
        <Select value={unit} onValueChange={(u) => handleChange(val, u)}>
          <SelectTrigger className="w-[85px] bg-white border-slate-350 text-slate-900 h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border">
            <SelectItem value="mins">Mins</SelectItem>
            <SelectItem value="hours">Hours</SelectItem>
            <SelectItem value="days">Days</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const SlaSettingsForm = ({ tenantConfig, setTenantConfig }) => {
  const [slaPolicy, setSlaPolicy] = useState({
    urgent: { t1: 15, t2: 30, t3: 60, resolution: 240 },
    normal: { t1: 60, t2: 120, t3: 240, resolution: 1440 },
    low: { t1: 120, t2: 240, t3: 480, resolution: 2880 }
  });
  
  const [shiftTimings, setShiftTimings] = useState({
    start_time: '09:00',
    end_time: '18:00',
    timezone: 'Asia/Kolkata'
  });
  
  const [savingSla, setSavingSla] = useState(false);

  useEffect(() => {
    if (tenantConfig) {
      if (tenantConfig.sla_policy && Object.keys(tenantConfig.sla_policy).length > 0) {
        setSlaPolicy(tenantConfig.sla_policy);
      }
      if (tenantConfig.shift_timings && Object.keys(tenantConfig.shift_timings).length > 0) {
        setShiftTimings(tenantConfig.shift_timings);
      }
    }
  }, [tenantConfig]);

  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  const handleSlaChange = (priority, key, mins) => {
    setSlaPolicy(prev => ({
      ...prev,
      [priority]: {
        ...prev[priority],
        [key]: mins
      }
    }));
  };

  const handleSaveSlaAndShift = async (e) => {
    e.preventDefault();
    if (!tenantConfig?.id) {
      toast.error('Tenant configuration context not loaded');
      return;
    }
    
    setSavingSla(true);
    try {
      const { data } = await tenantsApi.update(tenantConfig.id, {
        sla_policy: slaPolicy,
        shift_timings: shiftTimings
      });
      
      setTenantConfig(data);
      toast.success('SLA Policy and Shift Timings updated successfully!');
    } catch (error) {
      console.error('Failed to update SLA/Shift settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSavingSla(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* SLA Targets & Shift Card */}
      <Card className="p-6 border shadow-sm rounded-lg">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="p-2 bg-primary/10 rounded-sm text-primary">
            <Clock size={24} style={{ color: brandColorAccent }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-850">SLA Policy Configuration</h2>
            <p className="text-sm text-slate-500">Configure response (T1), warning (T2), assignment (T3), and resolution targets</p>
          </div>
        </div>

        <form onSubmit={handleSaveSlaAndShift} className="space-y-8 animate-fade-in-up">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* URGENT PRIORITY CARD */}
            <div className="p-5 border rounded-lg bg-red-50/20 border-red-100 space-y-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 pb-2 border-b border-red-100">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                <h3 className="font-bold text-red-900 uppercase tracking-wider text-xs">Urgent Priority</h3>
              </div>
              <div className="space-y-3">
                <SlaInput label="T1: Response SLA" priority="urgent" fieldKey="t1" policyState={slaPolicy} onChange={handleSlaChange} />
                <SlaInput label="T2: Warning SLA" priority="urgent" fieldKey="t2" policyState={slaPolicy} onChange={handleSlaChange} />
                <SlaInput label="T3: Assignment SLA" priority="urgent" fieldKey="t3" policyState={slaPolicy} onChange={handleSlaChange} />
                <SlaInput label="Resolution SLA" priority="urgent" fieldKey="resolution" policyState={slaPolicy} onChange={handleSlaChange} />
              </div>
            </div>

            {/* NORMAL PRIORITY CARD */}
            <div className="p-5 border rounded-lg bg-amber-50/20 border-amber-100 space-y-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 pb-2 border-b border-amber-100">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                <h3 className="font-bold text-amber-900 uppercase tracking-wider text-xs">Normal Priority</h3>
              </div>
              <div className="space-y-3">
                <SlaInput label="T1: Response SLA" priority="normal" fieldKey="t1" policyState={slaPolicy} onChange={handleSlaChange} />
                <SlaInput label="T2: Warning SLA" priority="normal" fieldKey="t2" policyState={slaPolicy} onChange={handleSlaChange} />
                <SlaInput label="T3: Assignment SLA" priority="normal" fieldKey="t3" policyState={slaPolicy} onChange={handleSlaChange} />
                <SlaInput label="Resolution SLA" priority="normal" fieldKey="resolution" policyState={slaPolicy} onChange={handleSlaChange} />
              </div>
            </div>

            {/* LOW PRIORITY CARD */}
            <div className="p-5 border rounded-lg bg-blue-50/20 border-blue-100 space-y-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 pb-2 border-b border-blue-100">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                <h3 className="font-bold text-blue-900 uppercase tracking-wider text-xs">Low Priority</h3>
              </div>
              <div className="space-y-3">
                <SlaInput label="T1: Response SLA" priority="low" fieldKey="t1" policyState={slaPolicy} onChange={handleSlaChange} />
                <SlaInput label="T2: Warning SLA" priority="low" fieldKey="t2" policyState={slaPolicy} onChange={handleSlaChange} />
                <SlaInput label="T3: Assignment SLA" priority="low" fieldKey="t3" policyState={slaPolicy} onChange={handleSlaChange} />
                <SlaInput label="Resolution SLA" priority="low" fieldKey="resolution" policyState={slaPolicy} onChange={handleSlaChange} />
              </div>
            </div>
          </div>

          {/* Shift Hours & Timezone */}
          <div className="pt-6 border-t border-slate-100 space-y-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Shift Timings & Operation Window</h3>
              <p className="text-xs text-slate-500">Define target agent business hours. SLA clocks automatically freeze outside shifts and skip weekends.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="startTime" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Shift Start Time
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={shiftTimings.start_time || '09:00'}
                  onChange={(e) => setShiftTimings({ ...shiftTimings, start_time: e.target.value })}
                  className="h-11 border-slate-300 text-slate-900 bg-white focus-ring"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Shift End Time
                </Label>
                <Input
                  id="endTime"
                  type="time"
                  value={shiftTimings.end_time || '18:00'}
                  onChange={(e) => setShiftTimings({ ...shiftTimings, end_time: e.target.value })}
                  className="h-11 border-slate-300 text-slate-900 bg-white focus-ring"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="timezone" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Operating Timezone
                </Label>
                <Select
                  value={shiftTimings.timezone || 'Asia/Kolkata'}
                  onValueChange={(val) => setShiftTimings({ ...shiftTimings, timezone: val })}
                >
                  <SelectTrigger className="h-11 border-slate-300 text-slate-900 bg-white focus-ring" data-testid="timezone-select">
                    <SelectValue placeholder="Select operating timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Asia/Kolkata">India Standard Time (IST - UTC+5:30)</SelectItem>
                    <SelectItem value="UTC">Coordinated Universal Time (UTC)</SelectItem>
                    <SelectItem value="America/New_York">Eastern Standard Time (EST - UTC-5)</SelectItem>
                    <SelectItem value="Europe/London">Greenwich Mean Time (GMT / BST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* FOC On-Leave Toggle */}
            <div className="mt-6 p-4 border rounded-lg bg-amber-50/40 border-amber-200/60">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">FOC Auto-Assign (On Leave Mode)</h4>
                  <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                    When enabled, approved tickets will be auto-assigned to the technician with the lowest active workload, bypassing FOC assignment.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShiftTimings(prev => ({ ...prev, foc_on_leave: !prev.foc_on_leave }))}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
                    shiftTimings.foc_on_leave ? 'bg-amber-500' : 'bg-slate-300'
                  }`}
                  data-testid="foc-on-leave-toggle"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      shiftTimings.foc_on_leave ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {shiftTimings.foc_on_leave && (
                <div className="mt-3 flex items-center gap-2 text-xs font-bold text-amber-700 bg-amber-100 border border-amber-200 px-3 py-2 rounded animate-fade-in">
                  <Warning size={14} weight="bold" />
                  Auto-assign is ACTIVE — all approved tickets bypass FOC queue
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-100">
            <Button 
              type="submit" 
              disabled={savingSla}
              className="font-bold text-white shadow hover:opacity-90 px-6 py-2 h-11"
              style={{ backgroundColor: brandColorAccent }}
            >
              {savingSla ? (
                <>
                  <ArrowClockwise className="animate-spin mr-2" size={18} />
                  Saving configurations...
                </>
              ) : (
                'Save SLA & Shift Configuration'
              )}
            </Button>
          </div>
        </form>
      </Card>

      {/* SLA Escalation Policy Flowchart */}
      <Card className="p-6 border shadow-sm rounded-lg">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="p-2 bg-primary/10 rounded-sm text-primary">
            <Warning size={24} style={{ color: brandColorAccent }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-850">SLA Breach Policy Flowchart</h2>
            <p className="text-sm text-slate-500">Visual escalation flowchart representing active system logic for SLA breaches</p>
          </div>
        </div>

        <div className="p-6 bg-slate-50/50 border border-slate-200 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative">
            
            {/* Step 1: T1 Response */}
            <div className="flex flex-col items-center bg-white border border-slate-200 p-5 rounded-lg shadow-sm text-center relative z-10">
              <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm mb-3">1</div>
              <h4 className="font-bold text-slate-900 text-sm">T1: Response SLA</h4>
              <p className="text-xs text-slate-500 mt-1">
                Customer submits ticket. Agent must pick it up within T1 window. Clock is shift-aware and skips weekends.
              </p>
              <div className="mt-4 px-2.5 py-1.5 bg-blue-50 border border-blue-100 rounded text-[10px] font-bold text-blue-700">
                Status: submitted
              </div>
              <div className="mt-2 px-2.5 py-1.5 bg-red-50 text-red-700 border border-red-100 rounded text-[10px] font-bold">
                Breach: WhatsApp Alert to Agents
              </div>
            </div>

            {/* Step 2: T2 FOC Escalation */}
            <div className="flex flex-col items-center bg-white border border-slate-200 p-5 rounded-lg shadow-sm text-center relative z-10">
              <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm mb-3">2</div>
              <h4 className="font-bold text-slate-900 text-sm">T2: FOC Escalation</h4>
              <p className="text-xs text-slate-500 mt-1">
                Warning escalation to Field Operations Coordinator if ticket is still unprocessed.
              </p>
              <div className="mt-4 px-2.5 py-1.5 bg-blue-50 border border-blue-100 rounded text-[10px] font-bold text-blue-700">
                Status: submitted / approved
              </div>
              <div className="mt-2 px-2.5 py-1.5 bg-amber-50 text-amber-700 border border-amber-100 rounded text-[10px] font-bold">
                Breach: Escalation Event Logged
              </div>
            </div>

            {/* Step 3: T3 Auto-Assignment */}
            <div className="flex flex-col items-center bg-white border border-slate-200 p-5 rounded-lg shadow-sm text-center relative z-10">
              <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm mb-3">3</div>
              <h4 className="font-bold text-slate-900 text-sm">T3: Auto-Assignment</h4>
              <p className="text-xs text-slate-500 mt-1">
                If FOC hasn't assigned a technician, system auto-assigns to the least loaded tech.
              </p>
              <div className="mt-4 px-2.5 py-1.5 bg-blue-50 border border-blue-100 rounded text-[10px] font-bold text-blue-700">
                Status: approved
              </div>
              <div className="mt-2 px-2.5 py-1.5 bg-red-50 text-red-700 border border-red-100 rounded text-[10px] font-bold">
                Breach: Auto-Assign + WhatsApp Alert
              </div>
            </div>

            {/* Step 4: Resolution */}
            <div className="flex flex-col items-center bg-white border border-slate-200 p-5 rounded-lg shadow-sm text-center relative z-10">
              <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm mb-3">4</div>
              <h4 className="font-bold text-slate-900 text-sm">Resolution SLA</h4>
              <p className="text-xs text-slate-500 mt-1">
                Total time from ticket transition to closure. Technician must resolve within this window.
              </p>
              <div className="mt-4 px-2.5 py-1.5 bg-blue-50 border border-blue-100 rounded text-[10px] font-bold text-blue-700">
                Status: any open
              </div>
              <div className="mt-2 px-2.5 py-1.5 bg-red-50 text-red-700 border border-red-100 rounded text-[10px] font-bold">
                Breach: Alert to Admin &amp; SLA Logged
              </div>
            </div>

          </div>
        </div>
      </Card>
    </div>
  );
};
